import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useProfiles } from '../context/ProfileContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_BANDS = [
  { value: '1-3', label: '1–3 jaar' },
  { value: '3-5', label: '3–5 jaar' },
  { value: '5-7', label: '5–7 jaar' },
  { value: '7-10', label: '7–10 jaar' },
  { value: '10+', label: '10+ jaar' },
];

const COLORS = ['#db8f2d', '#c47879', '#8baf5f', '#6c8db5', '#a47bb8', '#d2856e'];

const ProfileManage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { profiles, refresh, setActiveProfileId, activeProfile } = useProfiles();
  const [avatars, setAvatars] = useState([]);
  const [form, setForm] = useState({ name: '', avatar: '🦊', age_band: '3-5', color: '#db8f2d' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/profiles/options`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAvatars(r.data.avatars || []))
      .catch(() => {});
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error('Vul een naam in');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/profiles`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Welkom ${res.data.name}! 🎉`);
      setForm({ name: '', avatar: '🦊', age_band: '3-5', color: '#db8f2d' });
      await refresh();
      setActiveProfileId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon profiel niet toevoegen');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Profiel "${name}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/profiles/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Profiel verwijderd');
      await refresh();
    } catch (err) {
      toast.error('Verwijderen mislukt');
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      <div className="max-w-2xl mx-auto px-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground mb-4 hover:text-foreground">
          <ChevronLeft className="w-4 h-4" /> Terug
        </button>
        <h1 className="font-display text-3xl text-foreground mb-2">Kindprofielen</h1>
        <p className="text-sm text-muted-foreground font-accent mb-6">
          Maak een profiel voor elk kind. Zo kan je app met de juiste naam ‘Hoi {form.name || 'Sophie'}!’ groeten.
        </p>

        {/* Lijst bestaande */}
        {profiles.length > 0 && (
          <div className="space-y-2 mb-8">
            {profiles.map((p) => (
              <div
                key={p.id}
                data-testid={`profile-row-${p.id}`}
                className={`flex items-center gap-3 p-3 rounded-2xl border ${activeProfile?.id === p.id ? 'border-gold bg-gold/5' : 'border-border bg-card'}`}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-2xl shrink-0"
                  style={{ background: `${p.color || '#db8f2d'}22`, border: `2px solid ${p.color || '#db8f2d'}` }}
                >
                  {p.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-foreground">{p.name}</div>
                  {p.age_band && <div className="text-xs text-muted-foreground font-accent">{p.age_band} jaar</div>}
                </div>
                {activeProfile?.id === p.id ? (
                  <span className="text-[11px] font-bold text-gold flex items-center gap-1 px-2 py-1 rounded-full bg-gold/15">
                    <Check className="w-3 h-3" /> Actief
                  </span>
                ) : (
                  <button
                    onClick={() => setActiveProfileId(p.id)}
                    className="text-xs text-foreground bg-muted/40 hover:bg-muted px-3 py-1.5 rounded-full"
                    data-testid={`profile-activate-${p.id}`}
                  >
                    Activeer
                  </button>
                )}
                <button
                  onClick={() => remove(p.id, p.name)}
                  className="text-muted-foreground hover:text-destructive p-1.5"
                  data-testid={`profile-delete-${p.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {profiles.length >= 4 ? (
          <p className="text-sm text-muted-foreground bg-muted/30 rounded-xl p-4 text-center">
            Je hebt het maximum van 4 profielen bereikt.
          </p>
        ) : (
          <form onSubmit={submit} className="bg-card border border-border rounded-2xl p-5 space-y-4">
            <h2 className="font-display text-xl text-foreground flex items-center gap-2">
              <Plus className="w-5 h-5 text-old-rose" />
              Nieuw profiel
            </h2>

            <div>
              <label className="text-xs font-accent text-muted-foreground">Naam van het kind</label>
              <Input
                data-testid="profile-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Bijv. Sophie"
                maxLength={24}
                required
              />
            </div>

            <div>
              <label className="text-xs font-accent text-muted-foreground mb-2 block">Kies een avatar</label>
              <div className="grid grid-cols-8 gap-2">
                {avatars.map((a) => (
                  <button
                    key={a}
                    type="button"
                    data-testid={`avatar-pick-${a}`}
                    onClick={() => setForm((f) => ({ ...f, avatar: a }))}
                    className={`aspect-square rounded-xl flex items-center justify-center text-2xl transition-all ${
                      form.avatar === a
                        ? 'bg-gold/20 ring-2 ring-gold scale-110'
                        : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-accent text-muted-foreground mb-2 block">Leeftijd</label>
              <div className="flex gap-1.5 flex-wrap">
                {AGE_BANDS.map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, age_band: a.value }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                      form.age_band === a.value
                        ? 'bg-old-rose text-night-blue shadow'
                        : 'bg-muted/40 text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-accent text-muted-foreground mb-2 block">Themakleur</label>
              <div className="flex gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, color: c }))}
                    className={`w-9 h-9 rounded-full transition-transform ${form.color === c ? 'scale-110 ring-2 ring-foreground' : 'hover:scale-105'}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>

            <Button
              data-testid="profile-create-submit"
              type="submit"
              disabled={busy}
              size="lg"
              className="w-full rounded-full bg-old-rose hover:bg-old-rose/90 text-night-blue font-bold"
            >
              {busy ? 'Toevoegen...' : 'Profiel toevoegen'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfileManage;
